/*
 * Copyright © 2022 Firecrest Clinical, Ltd.
 * All rights reserved.
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import * as _ from 'lodash';
import {SUPPORTED_TYPE} from './app.constant';

/**
 * App service with Angular.
 */
@Injectable({
    providedIn: 'root'
})
export class AppService {
    
    /**
     * Current realmId.
     */
    realmId: string;

    /**
     * Current goid.
     */
    goid: string;

    /**
     * Window Reference.
     *
     */
    windowRef: any = window;

    /**
     * Document list.
     *
     */
    documents: any;

    /**
     * Document Ids.
     *
     */
    documentPks: any = [];

    /**
     * Document Cases.
     */
    documentCases: any = [];

    /**
     * Document Cases.
     * This property is using sent request to server side.
     */
    documentCasesKey: any = [];

    /**
     * Service constructor.
     *
     * @param httpClient HttpClient.
     * @param translate TranslateService.
     */
    constructor(
        private httpClient: HttpClient,
        private translate: TranslateService
    ) {
        this.goid = this.windowRef.FC.utils.getGoid();
        this.realmId = this.windowRef.FC.utils.getRealm();
    }

    /**
     * Get pdfTron license key.
     *
     * @returns Observable.
     */
    getPdftronLicenseKey() {
        const url = `/document-repository/rest/${this.realmId}/${this.goid}/pdftron/license`;
        return this.httpClient.get(url, { responseType: 'text' });
    }
    
    /**
     * Download document file.
     *
     * @param fileUrl FileUrl.
     * @returns Observable.
     */
    downloadDocumentFile(fileUrl: string) {
        return this.httpClient.get(fileUrl, {
            responseType: 'blob',
            observe: 'response'
        });
    }

    /**
     * Get file name.
     *
     * @param contentDisposition ContentDisposition.
     * @returns String.
     */
    getFileName(contentDisposition: string): string {
        const params = contentDisposition.split(';');
        const fileNames = params.filter(
            (param: string) => param.indexOf('filename=') !== -1
        );
        return fileNames.length ? fileNames[0].split('=')[1] : '';
    }

    /**
     * Get file extension.
     *
     * @param name Name.
     * @returns Boolean.
     */
    getFileExtension(name: string) {
        if (!name) {
            return 'pdf';
        }
        const ext = /^.+\.([^.]+)$/.exec(name);
        return ext == null ? '' : ext[1];
    }
    
    /**
     * Parse doc details.
     *
     * @param documents Documents.
     * @returns Object.
     */
    parseStandardDocumentDetails(documents: any) {
        let data = (documents || []).map((item: any) => {
            var documentPk = item.document?.documentPk;
            if (!this.documentPks.includes(documentPk)) {
                this.documentPks.push(documentPk);
            }
            const itemSites = item?.sites || item?.siteNumbers;
            return {
                documentId: item.documentId || item.document?.documentId,
                title: item.title || item.document?.title,
                categoryId: item.category || item.document?.categoryId,
                version: item.version || item.document?.version,
                revisionId: item.revisionId || item.document?.revisionId,
                versionDate: new Date(item.versionDate || item.document?.versionDate),
                publishDate: new Date(item.publishedDate || item.document?.publishedDate),
                language: item.language || item.document?.language,
                sites: Array.isArray(itemSites) ? itemSites.join(', ') : itemSites,
                url: item.document?.url ? item.document?.url : this.setDocumentUrl(item),
                isFromBulkAck: item?.isFromBulkAck ? true : false,
                fileName: item.filename || item.document?.filename,
                userAction: item?.userAction,
                readAcknowledge: item?.readAcknowledge,
                raActionStatus: item?.raActionStatus,
                documentAction: item?.documentAction || item?.document.documentAction
            };
        });
        data.sort((first: any, second:any) => first?.title.localeCompare(second?.title));
        return data;
    }

    /**
     * Parse doc cases details.
     *
     * @param documents Documents.
     * @returns Object.
     */
    parseDocumentCases(documents: any) {
        const map = new Map();
        (documents || []).forEach((document: any) => {
            document.forEach((item: any) => {
                const parsedItem = {
                    documentId: item.document?.documentId,
                    caseNumber: item.document?.caseNumber,
                    srCaseID: item.document?.srCaseID,
                    caseVersion: item.document?.caseVersion,
                    title: item.document?.title,
                    categoryId: item.document?.categoryId,
                    version: item.document?.version,
                    revisionId: item.document?.revisionId,
                    // versionDate: new Date(item.document?.versionDate),
                    publishDate: new Date(item.document?.publishedDate),
                    lineListingEndDate: item?.document?.lineListingEndDate,
                    lineListingStartDate: item?.document?.lineListingStartDate,
                    language: item.document?.language,
                    reportType: item.document?.reportType,
                    sites: Array.isArray(item?.sites) ? item?.sites.join(', ') : item?.sites,
                    url: item.document?.url ? item.document?.url : this.setDocumentUrl(item),
                    isFromBulkAck: item?.isFromBulkAck ? true : false,
                    userAction: item?.userAction,
                    fileName: item.document?.filename,
                    readAcknowledge: item?.readAcknowledge,
                    selectedFromPDUV: item?.selectedFromPDUV,
                    raActionStatus: item?.raActionStatus,
                    documentAction: item?.document.documentAction
                };
                const key = parsedItem.categoryId === 'pegasus_safety_letter'
                    ? `${parsedItem.caseNumber}-${parsedItem.caseVersion}`
                    : `${parsedItem.srCaseID}`;
                // If the key doesn't exist in the map, add it with an empty array
                if (!map.has(key)) {
                    map.set(key, []);
                    this.documentCases.push({
                        'caseNumber': btoa(parsedItem.caseNumber).replace(/\//g, '_'),
                        'caseVersion': this.windowRef.FC.utils.crypt.base64.encode(parsedItem.caseVersion)
                    });
                    this.documentCasesKey.push(key);
                }
                // Push the parsed item to the appropriate array in the map
                map.get(key).push(parsedItem);
            });
        });
        // Convert the map values to an array of arrays
        return Array.from(map.values());
    }

    /**
     * Retrieve all documents in pudv.
     *
     * @param requestParams RequestParams.
     * @param gridState GridState.
     * @param isStandard IsStandard.
     * @returns Observable.
     */
    public getPduvList(requestParams: any, gridState: any, isStandard: boolean): Observable<any> {
        let params = new HttpParams()
            .set('categoryGoid', requestParams?.categoryGoid)
            .set('categoryIds', requestParams?.categoryIds)
            .set('currentPage', gridState?.currentPage)
            .set('offset', (gridState.currentPage - 1) * gridState.pageSize)
            .set('order', gridState?.order)
            .set('orderBy', gridState?.orderBy)
            .set('pageSize', gridState?.pageSize);
        params = requestParams?.sites !== undefined ? params.set('sites', requestParams?.sites) : params;
        if (isStandard) {
            params = params.set('documentPks', encodeURIComponent(JSON.stringify(this.documentPks)));
        } else {
            params = params.set('documentCases', encodeURIComponent(JSON.stringify(this.documentCases)));
        }
        const url = `/document-repository/rest/${this.realmId}/${this.goid}/pduv/document`;
        
        return this.httpClient.get(url, {
            observe: 'response',
            params
        });
    }

    /**
     * Set document list.
     *
     * @param documents Documents.
     */
    setDocumentList(documents: any) {
        this.documents = documents;
    }

    /**
     * Set document url.
     *
     * @param document Document.
     * @returns String.
     */
    setDocumentUrl(document: any) {
        let site = this.getFirstSiteOfDocument(document);
        var url = this.windowRef.FC.utils.string.replaceMultiple('/document-repository/rest/{realmId}/{studyGoid}' +
            '/pduv/document/{docId}/revision/{revisionId}/content', {
            realmId: this.realmId,
            docId: document?.documentId || document?.document?.documentId,
            revisionId: document?.revisionId || document?.document?.revisionId,
            studyGoid: this.goid
        });
        return url;
    }

    /**
     * Get sites of document.
     *
     * @param document Document.
     * @returns Site.
     */
    getFirstSiteOfDocument(document: any) {
        if (_.get(document, 'icoTrialVisit.siteNumber') !== undefined) {
            return _.get(document, 'icoTrialVisit.siteNumber');
        }
        if (_.get(document, 'sites') !== undefined && _.get(document, 'sites').length === 1) {
            return  _.get(document, 'sites.0');
        }
        return null;
    };

    /**
     * Get document list.
     * 
     * @returns Documents.
     */
    getDocumentList() {
        return this.documents;
    }

    /**
     * Check is able to ack.
     *
     * @param documents Documents.
     * @param isPegasus IsPegasus.
     * @returns Boolean.
     */
    isAbleToAck(documents: any, isPegasus: boolean): boolean {
        let isAbleToAck: boolean;
        if (isPegasus) {
            isAbleToAck = documents.some((docCase: any) => 
                docCase.some((doc: any) => this.isReconditionToAck(doc))
            );
        } else {
            isAbleToAck = documents.some((doc: any) => this.isReconditionToAck(doc));
        }
        return isAbleToAck;
    }

    /**
     * Check is closed Rna document.
     * 
     * @param doc Documents.
     * @returns Boolean.
     */
    isClosedRnaDocument(doc: any): boolean {
        return doc && doc?.raActionStatus && doc?.raActionStatus?.action === 'close_read_and_acknowledge';
    }

    /**
     * Check is eligible RnA.
     * 
     * @param doc Documents.
     * @returns Boolean.
     */
    iseligibleRnA(doc: any): boolean {
        return doc?.readAcknowledge?.eligibleRnA;        
    }

    /**
     * Check is Not RnA Docs.
     * 
     * @param doc Documents.
     * @returns Boolean.
     */
    isNoRnADocs(doc: any): boolean {
        return doc?.userAction?.action === 'DOWNLOAD' || !doc?.userAction;
    }

    /**
     * Check is Not Recondition to Ack.
     * 
     * @param doc Documents.
     * @returns Boolean.
     */
    isReconditionToAck(doc: any): boolean {
        return this.iseligibleRnA(doc) && this.isNoRnADocs(doc) && !this.isClosedRnaDocument(doc);
    }
    
    /**
     * Find selected doc index.
     *
     * @param documents Documents.
     * @returns Index.
     */
    findSelectedDocIndex(documents: any) {
        const index = documents[0].findIndex((doc: any) => doc?.selectedFromPDUV);
        return index !== -1 ? index : 0; // Return index or null if not found  
    }

    /**
     * Find selected doc index.
     *
     * @param documents Documents.
     * @returns Index.
     */
    findSelectedDocumentForSingleCaaseIndex(documents: any) {
        const index = documents.findIndex((doc: any) => doc?.selectedFromPDUV);
        return index !== -1 ? index : 0; // Return index or null if not found  
    }

    /**
     * Check is supported file type.
     *
     * @param fileName FileName.
     * @returns Boolean.
     */
    isSupportedFileType = (fileName: any) => {
        if (!fileName) {
            return false;
        }
        const extension = fileName.split('.').pop().toLowerCase();
        return SUPPORTED_TYPE.includes(extension);
    };

    /**
     * Get Document Pks.
     * 
     * @returns DocumentPks.
     */
    getDocumentPks() {
        return this.documentPks;
    }

    /**
     * Set Document Pks.
     * 
     * @param documentPks DocumentPks.
     */
    setDocumentPks(documentPks: any) {
        this.documentPks = documentPks;
    }

    /**
     * Get Document Cases.
     * 
     * @returns DocumentCases.
     */
    getDocumentCases() {
        return this.documentCases;
    }

    /**
     * Set Document Cases.
     * 
     * @param documentCases DocumentCases.
     */
    setDocumentCases(documentCases: any) {
        this.documentCases = documentCases;
    }

    /**
     * Get Document Cases Key.
     * 
     * @returns DocumentCasesKey.
     */
    getDocumentCasesKey() {
        return this.documentCasesKey;
    }

    /**
     * Set Document Cases Key.
     * 
     * @param documentCasesKey DocumentCasesKey.
     */
    setDocumentCasesKey(documentCasesKey: any) {
        this.documentCasesKey = documentCasesKey;
    }
} /* End */
